import { type Spec } from "@json-render/react";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** json-render Spec 最小结构校验（root + elements[root]） */
export const isValidJsonRenderSpec = (value: unknown): value is Spec => {
  if (!isRecord(value)) {
    return false;
  }

  const { root, elements } = value;

  return (
    typeof root === "string" &&
    root.length > 0 &&
    isRecord(elements) &&
    isRecord(elements[root])
  );
};

const DYNAMIC_UI_PATTERN = /^<dynamic_ui(?:\s+[^>]*)?>([\s\S]*)<\/dynamic_ui>$/;
const DYNAMIC_UI_OPEN_TAG_PATTERN = /^<dynamic_ui([^>]*)>/;

export type DynamicUiTagAttributes = {
  collapsed?: boolean;
  id?: string;
  metrics?: string;
};

const parseBooleanAttribute = (
  value: string | undefined,
): boolean | undefined => {
  if (value === undefined) {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  if (normalized === "true") {
    return true;
  }
  if (normalized === "false") {
    return false;
  }
  return undefined;
};

const parseStringAttribute = (
  openingTag: string,
  attributeName: string,
): string | undefined => {
  const attributeRegex = new RegExp(
    `${attributeName}\\s*=\\s*(?:"([^"]*)"|'([^']*)')`,
  );
  const value = openingTag.match(attributeRegex)?.[1]?.trim();
  return value ? value : undefined;
};

/** 解析 `<dynamic_ui ...>` opening tag 上的 collapsed / id / metrics 属性。 */
export const parseDynamicUiTagAttributes = (
  body: string,
): DynamicUiTagAttributes => {
  const matched = body.trim().match(DYNAMIC_UI_OPEN_TAG_PATTERN);
  if (!matched?.[1]) {
    return {};
  }

  const openingTag = `<dynamic_ui${matched[1]}>`;
  const collapsed = parseBooleanAttribute(
    parseStringAttribute(openingTag, "collapsed"),
  );
  const id = parseStringAttribute(openingTag, "id");
  const metrics = parseStringAttribute(openingTag, "metrics");

  return {
    ...(collapsed === undefined ? {} : { collapsed }),
    ...(id ? { id } : {}),
    ...(metrics === undefined ? {} : { metrics }),
  };
};

/**
 * 解析整段为单个 `<dynamic_ui>...</dynamic_ui>` 包裹的 json-render Spec。
 * 用于 Tracker / Notification WS 等 payload。
 */
export const parseDynamicUiBody = (body: string): Spec | null => {
  const matched = body.trim().match(DYNAMIC_UI_PATTERN);

  if (!matched) {
    return null;
  }

  const rawJson = matched[1]?.trim();

  if (!rawJson) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawJson);
    return isValidJsonRenderSpec(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
